import { useEffect, useMemo, useState } from 'react'
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { useUser } from '@clerk/clerk-react'
import { useWorkspaceStore, type User } from '../stores/workspaceStore'
import { usersApi, subscriptionApi, type SubscriptionStatus } from '../services/api'
import { canAccessFeature, roleLabel, type Role } from '../utils/permissions'
import { useWorkspaceRole } from '../utils/useWorkspaceRole'
import NotificationsSection from '../components/setup/NotificationsSection'
import TeamView from '../components/TeamView'
import UsageMeter from '../components/UsageMeter'
import {
  Avatar,
  Button,
  Card,
  CardFooter,
  CardHeader,
  EmptyState,
  Field,
  FieldGrid,
  Icon,
  KeyValue,
  LoadingState,
  PageHeader,
  Stack,
  Stat,
  TabPanel,
  Tabs,
} from '../ds'

/**
 * Account — what is genuinely account-level, and nothing else.
 *
 * ⚠️ **Store and Integrations left in App IA v5 phase 2c PR 2.** Their content is now
 * Authority's Store and Channels tabs (`components/setup/{Store,Channels}Section.tsx`),
 * per `settings-consolidation.md` §3.6. Both old `?tab=` values redirect rather than
 * falling back to a default, because they were linkable and one of them was the default
 * tab — a bookmark landing silently on Billing is worse than a redirect.
 *
 * ⚠️ `NotificationsSection` stays here. §3.6 folds it into Authority; that is the spec's
 * open question 2 and is not in `MAR-108`'s acceptance criteria. Named, not done.
 */

/**
 * Where the two relocated tabs went — old `?tab=` value → the Authority tab that now owns it.
 *
 * ⚠️ A `Map`, not an object literal. `RELOCATED['toString']` on a bare object is a truthy
 * `Function`, which react-router would take as a `Path`, resolve to the current pathname and
 * land on `/account` with the query string silently dropped. The `TAB_VALUES.includes(...)`
 * guard this replaced had no such hole.
 */
const RELOCATED = new Map<string, string>([
  ['store', 'store'],
  ['integrations', 'channels'],
])

/**
 * The Authority URL for a relocated tab, carrying every OTHER parameter across.
 *
 * ⚠️ Merge, never replace. `screens/Authority.tsx` goes out of its way to preserve
 * `?tab=limits&from=ledger&decision=abc` when a merchant clicks a second tab — a receipt link
 * out of the Ledger is the whole reason that parameter exists. Rebuilding the target as a bare
 * string here would throw that context away one hop earlier, before Authority ever saw it.
 */
function relocatedTarget(params: URLSearchParams, destination: string): string {
  const next = new URLSearchParams(params)
  next.set('tab', destination)
  return `/authority?${next.toString()}`
}

type Tab = 'billing' | 'team' | 'notifications' | 'profile'

const TABS: { key: Tab; label: string; icon: string }[] = [
  { key: 'billing', label: 'Billing', icon: 'M3 6h18v12H3zM3 10h18' },
  { key: 'team', label: 'Team', icon: 'M9 8a3 3 0 1 0 0 .01M3 20a6 6 0 0 1 12 0M16 6a3 3 0 0 1 0 5' },
  { key: 'notifications', label: 'Notifications', icon: 'M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9M10 21h4' },
  { key: 'profile', label: 'Profile', icon: 'M12 8a3.4 3.4 0 1 0 0 .01M5.5 20a6.5 6.5 0 0 1 13 0' },
]


const TAB_ITEMS = TABS.map((t) => ({ value: t.key, label: t.label, icon: <Icon d={t.icon} /> }))
const ID_BASE = 'account'

const TAB_VALUES: Tab[] = ['billing', 'team', 'notifications', 'profile']

export default function Account() {
  const navigate = useNavigate()
  const { user } = useUser()
  const [searchParams, setSearchParams] = useSearchParams()
  const { currentAccount, currentUser } = useWorkspaceStore()
  const { role } = useWorkspaceRole()

  const requested = searchParams.get('tab') || ''
  const initialTab = requested as Tab
  const [tab, setTab] = useState<Tab>(TAB_VALUES.includes(initialTab) ? initialTab : 'billing')

  // ⚠️ Merge, never replace. `setSearchParams({ tab: t })` drops every other parameter —
  // the same defect `relocatedTarget` above exists to avoid, one function away, and the
  // one `screens/Authority.tsx` documents at length. It survived here only because this
  // screen's suite used to mock `useSearchParams` to a frozen empty value and so could
  // not express a second parameter to lose.
  //
  // This screen still mirrors the tab into `useState`, which is a separate (and also
  // pre-existing) problem: an in-app navigation to `/account?tab=team` while already here
  // moves the URL and not the tab, and Back walks the pushed entries without moving it.
  // `Authority.tsx` derives the tab at the read instead and is the pattern to copy. Not
  // changed here — that is a behaviour change to a screen this PR is only shrinking.
  const setActive = (t: Tab) => {
    setTab(t)
    const next = new URLSearchParams(searchParams)
    next.set('tab', t)
    setSearchParams(next)
  }

  // Ahead of the permission gate: a link to a tab that MOVED should reach where it
  // moved to, not be answered with "access restricted" for a screen that no longer
  // owns it.
  const destination = RELOCATED.get(requested)
  if (destination) {
    return <Navigate to={relocatedTarget(searchParams, destination)} replace />
  }

  // The role from `useWorkspaceRole` — the same hook every Authority tab gates on — never
  // `currentUser.role` directly: two gates reading two sources let one person be an owner here
  // and a member on Authority at once (PR #237 round 3).
  if (!canAccessFeature(role, 'account')) {
    return (
      <div className="mv-canvas-col">
        <EmptyState eyebrow="Account" title="Access restricted">
          You don&apos;t have permission to view account settings. Ask a workspace owner or admin for access.
        </EmptyState>
      </div>
    )
  }

  return (
    <div className="mv-canvas-col">
      <PageHeader
        eyebrow="Account"
        title="Your plan & your team."
        sub={
          <>
            Your plan and usage, the people who can see her, and how she lets you know something needs you. Your store
            and the channels she works through are under <b>Authority</b>.
          </>
        }
      />

      {/* Underline tabs (D-18): tab roles, arrow keys move between them. */}
      <Tabs tabs={TAB_ITEMS} value={tab} onChange={(v) => setActive(v as Tab)} label="Account sections" idBase={ID_BASE} />

      {TABS.map((t) => (
        <TabPanel key={t.key} value={t.key} active={tab} idBase={ID_BASE}>
          {t.key === 'billing' && (
            <BillingPanel accountId={currentAccount?.id} onChangePlan={() => navigate('/pricing')} />
          )}
          {/* Team reuses the existing, schema-accurate members/invite/role component. */}
          {t.key === 'team' && <TeamView />}
          {/* Governance V2 notification prefs + pending-approval queue. */}
          {t.key === 'notifications' && <NotificationsSection onClose={() => {}} />}
          {t.key === 'profile' && (
            <ProfilePanel currentUser={currentUser} role={role} clerkInitial={user?.firstName?.[0]} />
          )}
        </TabPanel>
      ))}
    </div>
  )
}

/* -------------------------------------------------------------- BILLING */

const METER_LABELS: { key: 'mau' | 'conversations' | 'emails'; label: string; sage?: boolean }[] = [
  { key: 'mau', label: 'Active customers' },
  { key: 'conversations', label: 'Conversations' },
  { key: 'emails', label: 'Emails', sage: true },
]

function BillingPanel({ accountId, onChangePlan }: { accountId?: string; onChangePlan: () => void }) {
  const [status, setStatus] = useState<SubscriptionStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [portalLoading, setPortalLoading] = useState(false)

  useEffect(() => {
    if (!accountId) {
      setLoading(false)
      return
    }
    subscriptionApi
      .getStatus(accountId)
      .then((r) => setStatus(r.success && r.data ? r.data : null))
      .catch(() => setStatus(null))
      .finally(() => setLoading(false))
  }, [accountId])

  const plan = useMemo(() => status?.availableTiers?.find((t) => t.id === status.tier), [status])
  const isTrial = status?.status === 'trialing'
  // Expired trial with no paid plan: the assistant is paused, not billing at the
  // Starter price. Without this it fell through to the "paid plan" branches and
  // showed "$79 per month" + an estimated bill as if subscribed.
  const isExpired = status?.status === 'expired'
  const hasStripe = !!status?.stripeCustomerId && status?.source === 'stripe'
  const isEnterprise = status?.tier === 'enterprise'

  const openPortal = async () => {
    if (!accountId) return
    setPortalLoading(true)
    try {
      const r = await subscriptionApi.createPortal(accountId, { returnUrl: window.location.href })
      if (r.success && r.data?.portalUrl) window.location.href = r.data.portalUrl
    } finally {
      setPortalLoading(false)
    }
  }

  if (loading) {
    return <LoadingState caption="Loading your plan…" />
  }
  if (!status) {
    return (
      <Card>
        <CardHeader title="Billing" description="We couldn’t load your subscription right now." />
        <CardFooter>
          <Button variant="ghost" size="sm" onClick={onChangePlan}>
            View plans
          </Button>
        </CardFooter>
      </Card>
    )
  }

  const planName = isExpired
    ? 'Trial ended'
    : plan?.name || (isTrial ? 'Free trial' : status.tier.charAt(0).toUpperCase() + status.tier.slice(1))
  const resets = status.period?.endsAt
    ? new Date(status.period.endsAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })
    : null

  return (
    <Stack>
      <Card tone="gold">
        <CardHeader
          title={planName}
          description={
            isExpired
              ? 'Your free trial has ended · upgrade to reactivate Marva'
              : isTrial
              ? `Trial · ${status.period?.daysRemaining ?? 0} days remaining · same assistant as every plan`
              : 'Same assistant as every plan — the limits scale, she doesn’t change'
          }
          aside={
            plan && !isExpired ? (
              <Stat value={plan.monthlyPrice} label="per month" tone="gold" />
            ) : (
              <Stat value={isExpired ? 'Paused' : isTrial ? 'Free' : '—'} label="" />
            )
          }
        />
      </Card>

      <Card>
        <CardHeader
          title="Plan & usage"
          description={resets ? `Resets ${resets}.` : 'Your current usage against plan limits.'}
        />
        <Stack gap="sm">
          {METER_LABELS.map(({ key, label, sage }) => {
            const m = status.usage?.[key]
            if (!m) return null
            return <UsageMeter key={key} label={label} metric={m} sage={sage} />
          })}
        </Stack>
        {status.billEstimate && !isTrial && !isExpired && (
          <KeyValue
            items={[
              {
                // Short enough for the key column: "Estimated bill this period" wrapped onto two
                // lines beside its value. The period moves into the value.
                label: 'Estimated bill',
                value: `$${(status.billEstimate.totalCents / 100).toFixed(2)} this period${
                  status.billEstimate.overageCents > 0
                    ? ` ($${(status.billEstimate.baseCents / 100).toFixed(0)} plan + $${(
                        status.billEstimate.overageCents / 100
                      ).toFixed(2)} overage)`
                    : ''
                }`,
              },
            ]}
          />
        )}
        <CardFooter>
          <Button variant="ghost" size="sm" onClick={onChangePlan}>
            Change plan
          </Button>
        </CardFooter>
      </Card>

      <Card>
        <CardHeader
          title="Payment & invoices"
          description={
            isEnterprise
              ? 'Your plan is invoiced directly by Marva. Contact support for billing or plan changes.'
              : hasStripe
                ? 'Your card, billing history and invoices are managed securely in the Stripe billing portal.'
                : 'Start a subscription to manage your payment method and download invoices.'
          }
        />
        <CardFooter>
          {isEnterprise ? (
            <Button variant="ghost" size="sm" href="mailto:support@marva.co?subject=Billing">
              Contact support
            </Button>
          ) : hasStripe ? (
            <Button variant="ghost" size="sm" busy={portalLoading} busyLabel="Opening…" onClick={openPortal}>
              {portalLoading ? 'Opening…' : 'Manage billing in Stripe'}
            </Button>
          ) : (
            <Button size="sm" onClick={onChangePlan}>
              Choose a plan
            </Button>
          )}
        </CardFooter>
      </Card>
    </Stack>
  )
}

/* -------------------------------------------------------------- PROFILE */

/**
 * The name fields' starting values. A membership row often carries only `name` — the one Team
 * shows — with no `firstName`/`lastName`, which left both fields blank for a user Team names in
 * full. Split fields win when stored; otherwise `name` splits at its first space.
 */
function profileNames(user: Pick<User, 'name' | 'firstName' | 'lastName'> | null): { firstName: string; lastName: string } {
  if (!user) return { firstName: '', lastName: '' }
  if (user.firstName || user.lastName) return { firstName: user.firstName || '', lastName: user.lastName || '' }
  const [first = '', ...rest] = (user.name || '').trim().split(/\s+/)
  return { firstName: first, lastName: rest.join(' ') }
}

/**
 * `role` is `useWorkspaceRole().role` — the role the gate above opened on — never
 * `currentUser.role`, which can describe another workspace (PR #237 round 4: owner in A, member
 * in B, on A with a stale `currentUser` read "Member" here while the gate opened as owner).
 * Worded by `roleLabel` (round 5: never the raw gate value). `null` (no membership row here)
 * shows a neutral "—" — not the pre-PR guessed "member".
 */
function ProfilePanel({
  currentUser,
  role,
  clerkInitial,
}: {
  currentUser: User | null
  role: Role | null
  clerkInitial?: string
}) {
  const stored = profileNames(currentUser)
  const [firstName, setFirstName] = useState(stored.firstName)
  const [lastName, setLastName] = useState(stored.lastName)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setFirstName(stored.firstName)
    setLastName(stored.lastName)
    // Re-sync only when the active user changes, not on every field edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id])

  // Against the same derived baseline the fields were seeded from, or a prefilled name reads as an edit.
  const changed = firstName.trim() !== stored.firstName || lastName.trim() !== stored.lastName
  const initial = (stored.firstName[0] || clerkInitial || 'U').toUpperCase()

  const save = async () => {
    if (!currentUser?.id || !firstName.trim() || !lastName.trim()) return
    setSaving(true)
    try {
      await usersApi.updateProfile(currentUser.id, {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        name: `${firstName.trim()} ${lastName.trim()}`,
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card>
      <CardHeader
        title="Your profile"
        description="How you appear to your team inside Marva."
        icon={<Avatar name={initial} size="xl" tone="gold" label={false} />}
      />
      <Stack>
        <FieldGrid>
          <Field label="First name" value={firstName} onChange={(e) => setFirstName((e.target as HTMLInputElement).value)} placeholder="First name" />
          <Field label="Last name" value={lastName} onChange={(e) => setLastName((e.target as HTMLInputElement).value)} placeholder="Last name" />
        </FieldGrid>
        <FieldGrid>
          <Field label="Email" type="email" value={currentUser?.email || ''} disabled />
          <Field label="Role" value={roleLabel(role) ?? '—'} disabled />
        </FieldGrid>
      </Stack>
      <CardFooter>
        <Button variant="ghost" size="sm" disabled={!changed} busy={saving} busyLabel="Saving…" onClick={save}>
          Save changes
        </Button>
      </CardFooter>
    </Card>
  )
}
